
import { Box, Card, CircularProgress, Divider, Grid, Icon, IconButton, InputLabel, Modal, Paper, Table, TableBody, TableCell, TableContainer, TableRow, debounce } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { Form, Formik, useFormikContext } from "formik";
import { useEffect, useState, useCallback, useMemo } from "react";
import { toDate, toDateReqFormat, toFirstDay } from "utils/DateUtils";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post, postNoResponse } from "utils/WebService";


import * as Yup from 'yup';

const formatCurrency = (val) => val === undefined || val === null ? 'R$ 0,00' : 'R$ ' + Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const disabledStyle = {
    "& .MuiInputBase-input.Mui-disabled": {
        backgroundColor: "#f0f2f5",
        WebkitTextFillColor: "#7b809a",
    },
};

const FetchAndSetData = ({
    id,
    setIsLoadingInfo,
    closeCreateEdit,
    setAreaList,
    setAcaoList,
    setCidadeList,
    setForumList,
    tipoIndiceEnum,
    tipoJusticaEnum,
    tipoComplementoJusticaEnum,
    motivoEncerramentoEnum
}) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/processo/by-id/${id}`).then(data => {
                const d = data?.cadastroBasico || {};
                
                setFieldValue("id", d.id || null);
                
                if (d.caso) {
                    setFieldValue("caso", { 
                        id: d.caso.id, 
                        nomeCaso: d.caso.nomeCaso, 
                        label: d.caso.nomeCaso, 
                        value: d.caso.nomeCaso 
                    });
                } else {
                    setFieldValue("caso", null);
                }
                
                setFieldValue("relacao", d.relacao || null);
                const areaObj = d.area || null;
                const grupoAreaObj = areaObj?.grupoArea || null;
                setFieldValue("area", areaObj);
                setFieldValue("grupoArea", grupoAreaObj);
                const statusCode = d.status?.code || d.status;
                setFieldValue("statusProcess", statusCode ? { code: statusCode, name: statusCode === 'closed' ? "Encerrado" : "Ativo" } : null);
                setFieldValue("equipe", d.equipe || null);
                setFieldValue("advogado", d.advogado || null);
                
                const currentJusticaCode = d.justica;
                const foundJustica = tipoJusticaEnum?.find(i => i.code === currentJusticaCode);
                setFieldValue("justica", foundJustica || (currentJusticaCode ? { code: currentJusticaCode } : null));
                
                setFieldValue("faseProcesso", d.faseProcesso || null);
                
                const currentCompCode = d.complementoJustica;
                const foundComp = tipoComplementoJusticaEnum?.find(i => i.code === currentCompCode);
                setFieldValue("complementoJustica", foundComp || (currentCompCode ? { code: currentCompCode } : null));
                
                setFieldValue("tipoAcao", d.acao || null);
                setFieldValue("orgao", d.orgao || null);
                setFieldValue("risco", d.risco || null);
                setFieldValue("forum", d.forum || null);
                setFieldValue("estado", d.forum?.municipio?.estado || null);
                setFieldValue("cidade", d.forum?.municipio || null);
                setFieldValue("complementoVara", d.complementoVara || null);
                
                const currentIndiceCode = d.indice;
                const foundIndice = tipoIndiceEnum?.find(i => i.code == currentIndiceCode);
                setFieldValue("indice", foundIndice || (currentIndiceCode ? { code: currentIndiceCode } : null));
                
                setFieldValue('numAutomatico', d.numAutomatico || "");
                setFieldValue('nprocessoSegundo', d.numProcessoSegunda ? { processo: d.numProcessoSegunda, label: d.numProcessoSegunda } : null);
                setFieldValue('nprocessoTerceiro', d.numProcessoTerceira ? { processo: d.numProcessoTerceira, label: d.numProcessoTerceira } : null);
                setFieldValue('npastaCliente', d.numPastaCliente || "");
                setFieldValue('vara', d.vara || "");
                setFieldValue('varaEncontrada', d.varaEncontrada || "");
                setFieldValue('observacao', d.observacoes || "");
                setFieldValue('ano', d.ano || "");
                setFieldValue('dataDistribuicao', d.dataDistribuicao || "");
                setFieldValue('inicioCorrecao', d.inicioCorrecao || "");
                setFieldValue('ultCorrecao', d.ultimaCorrecao || "");
                setFieldValue('pagamentosTotais', d.pagamentosTotais || 0);
                setFieldValue('recebimentosTotais', d.recebimentosTotais || 0);
                setFieldValue('justicaGratuita', d.justicaGratuita || "");
                setFieldValue('situacao', d.situacao || null);
                
                const currentMotivoCode = d.motivoEncerramento;
                const foundMotivo = motivoEncerramentoEnum?.find(i => i.code === currentMotivoCode);
                setFieldValue('motivo', foundMotivo || (currentMotivoCode ? { code: currentMotivoCode } : null));

                setFieldValue('tabelaData', {
                    causa: { 
                        nominal: d.valorCausaNominal ?? 0, 
                        corrigido: d.valorCausaCorrigido ?? 0, 
                        corrigidoJuros: 0 
                    },
                    pedido: { 
                        nominal: d.valorPedidoNominal ?? 0, 
                        corrigido: d.valorPedidoCorrigido ?? 0, 
                        corrigidoJuros: d.valorPedidoCorrigidoJuros ?? 0 
                    },
                    resultado: { nominal: 0, corrigido: 0, corrigidoJuros: 0 }
                });

                if(grupoAreaObj?.id) {
                    get(`/area/${grupoAreaObj.id}/list-all`).then(({list}) => setAreaList(list)).catch(() => {});
                }
                if(d.justica) {
                    const code = typeof d.justica === 'object' ? d.justica.code : d.justica;
                    get(`/acao/${code}/list-all`).then(({list}) => setAcaoList(list)).catch(() => {});
                }
                const forumObj = d.forum;
                if(forumObj?.municipio?.estado?.id) {
                    get(`/addresses/cities?stateId=${forumObj.municipio.estado.id}`).then(({list}) => setCidadeList(list)).catch(() => {});
                }
                if(forumObj?.municipio?.id) {
                    get(`/forum/${forumObj.municipio.id}/list-all`).then(({list}) => setForumList(list)).catch(() => {});
                }

            }).catch((erro) => {
                console.error(erro);
                errorAlert("Erro ao buscar dados do caso!", closeCreateEdit);
            }).finally(() => setIsLoadingInfo(false));
        }
    }, [id]);

    return null;
};

const ModalCriarCaso = ({ open, handleClose, defaultNomeCaso, onCreated }) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [pessoaList, setPessoaList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (open) {
            get("/pessoa/list-all").then(({ list }) => setPessoaList(list || [])).catch(() => {});
        }
    }, [open]);

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="modal-criar-caso">
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '90%', md: 500 }, bgcolor: darkMode ? '#1e1e1e' : 'background.paper',
                boxShadow: 24, p: 4, borderRadius: 2
            }}>
                <MDTypography variant="h6" mb={2}>Cadastrar Novo Caso Simplificado</MDTypography>
                <Formik
                    initialValues={{ pessoa: null, nomeCaso: defaultNomeCaso, documento: "" }}
                    validationSchema={Yup.object().shape({
                        pessoa: Yup.object().required("Selecione um cliente"),
                        nomeCaso: Yup.string().required("Informe o nome do caso")
                    })}
                    onSubmit={(values, actions) => {
                        setIsLoading(true);
                        const payload = {
                            pessoaId: values.pessoa?.id,
                            nomeCaso: values.nomeCaso,
                            documento: values.documento,
                        };
                        postNoResponse("/processo/save-caso", payload)
                            .then(() => {
                                successAlert("Caso cadastrado com sucesso!");
                                actions.setSubmitting(false);
                                onCreated();
                            })
                            .catch(() => {
                                errorAlert("Erro ao salvar caso!");
                                actions.setSubmitting(false);
                            })
                            .finally(() => setIsLoading(false));
                    }}
                >
                    {({ values, errors, touched, setFieldValue, isSubmitting, handleSubmit }) => (
                        <Form autoComplete="off">
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Cliente</InputLabel>
                                    <MDSelect
                                        options={pessoaList}
                                        getOptionLabel={o => o.nomeComum}
                                        getOptionValue={o => o.id}
                                        value={values.pessoa}
                                        onChange={v => setFieldValue("pessoa", v)}
                                        error={errors.pessoa && touched.pessoa}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Nome do Caso</InputLabel>
                                    <MDInput
                                        size="small" fullWidth
                                        value={values.nomeCaso}
                                        onChange={({ target }) => setFieldValue("nomeCaso", target.value)}
                                        error={errors.nomeCaso && touched.nomeCaso}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Documento</InputLabel>
                                    <MDInput
                                        size="small" fullWidth
                                        value={values.documento}
                                        onChange={({ target }) => setFieldValue("documento", target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
                                <MDButton color="secondary" variant="outlined" onClick={handleClose}>Cancelar</MDButton>
                                <MDButton color="info" variant="contained" type="button" onClick={handleSubmit} disabled={isSubmitting || isLoading}>
                                    {isLoading || isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
                                </MDButton>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Modal>
    );
};

const AdvogadoSelector = ({ isLoadingInfo, values, setFieldValue }) => {
    const [advogadosList, setAdvogadosList] = useState([]);
    const [isLoadingAdvogados, setIsLoadingAdvogados] = useState(false);

    useEffect(() => {
        if (values.equipe && values.equipe.id) {
            setIsLoadingAdvogados(true);
            get(`/equipe/by-id/${values.equipe.id}`)
                .then(res => {
                    setAdvogadosList(res?.usuarios || []);
                })
                .catch(err => {
                    console.error(err);
                    setAdvogadosList([]);
                })
                .finally(() => setIsLoadingAdvogados(false));
        } else {
            setAdvogadosList([]);
        }
    }, [values.equipe]);

    return (
        <Grid item xs={12} sm={3}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Advogado Responsável</InputLabel>
            <MDSelect
                isLoading={isLoadingInfo || isLoadingAdvogados}
                options={advogadosList}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.nome}
                value={values.advogado}
                onChange={v => setFieldValue("advogado", v)}
                disabled={!values.equipe}
            />
        </Grid>
    );
};

export default function BasicRegistration({ id, closeCreateEdit, processoEletronico, reloadData, handleOpenContratos }) {
    const [controller] = useMaterialUIController();
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [isProc, setIsProc] = useState(false);
    const [isLoadingCalc, setLoadingCalc] = useState(false);
    const [modalCasoOpen, setModalCasoOpen] = useState(false);
    const [modalCasoDefaultNome, setModalCasoDefaultNome] = useState("");
    const { darkMode } = controller;

    const {
        motivoEncerramentoEnum,
        tipoJusticaEnum,
        tipoComplementoJusticaEnum,
        tipoIndiceEnum,
        statusProcesso
    } = useUser();

    const [grupoAreaList, setGrupoAreaList] = useState([]);
    const [areaList, setAreaList] = useState([]);
    const [relacaoList, setRelacaoList] = useState([]);
    const [equipeList, setEquipeList] = useState([]);
    const [acaoList, setAcaoList] = useState([]);
    const [orgaoList, setOrgaoList] = useState([]);
    const [forumList, setForumList] = useState([]);
    const [estadoList, setEstadoList] = useState([]);
    const [cidadeList, setCidadeList] = useState([]);
    const [complementoVaraList, setComplementoVaraList] = useState([]);
    const [riscoList, setRiscoList] = useState([]);
    const [situacaoList, setSituacaoList] = useState([]);
    const [faseProcessoList, setFaseProcessoList] = useState([]);
    const [processosPesquisa, setProcessosPesquisa] = useState([]);
    const [isLoadingProcessos, setIsLoadingProcessos] = useState(false);

    const buscarProcessos = async (search) => {
        if (!search || search.trim() === "") {
            setProcessosPesquisa([]);
            return;
        }
        setIsLoadingProcessos(true);
        get(`/processo/list-all?search=${encodeURIComponent(search.replace(/\D/g, ''))}`)
            .then((res) => {
                setProcessosPesquisa(res.list || []);
            })
            .catch(() => {
                setProcessosPesquisa([]);
            })
            .finally(() => setIsLoadingProcessos(false));
    };

    const buscarProcessosDebounced = debounce(buscarProcessos, 400);

    const [casosSimplesPesquisa, setCasosSimplesPesquisa] = useState([]);
    const [isLoadingCasosSimples, setIsLoadingCasosSimples] = useState(false);

    const buscarCasosSimples = async (search) => {
        setIsLoadingCasosSimples(true);
        const payload = {
            page: 0,
            pageSize: 50,
            sorted: [],
            filtered: search && search.trim() !== "" ? [{ id: "nomeCaso", value: search }] : []
        };
        post("/processo/list-casos", payload)
            .then(({ data }) => setCasosSimplesPesquisa(data || []))
            .catch(() => setCasosSimplesPesquisa([]))
            .finally(() => setIsLoadingCasosSimples(false));
    };

    const buscarCasosSimplesDebounced = debounce(buscarCasosSimples, 400);

    useEffect(() => {
        if (processoEletronico?.code?.includes("eproc")) {
            setIsProc(true)
        } else {
            setIsProc(false)
        }
    }, [processoEletronico]);

    useEffect(() => {
        get("/grupo-area/list-all").then(({list}) => setGrupoAreaList(list || [])).catch(() => {});
        get("/relacao/list-all").then(({list}) => setRelacaoList(list || [])).catch(() => {});
        get("/equipe/list-all").then(({list}) => setEquipeList(list || [])).catch(() => {});
        get("/orgao/list-all").then(({list}) => setOrgaoList(list || [])).catch(() => {});
        get("/risco/list-all").then(({list}) => setRiscoList(list || [])).catch(() => {});
        get("/fase-processo/list-all").then(({list}) => setFaseProcessoList(list || [])).catch(() => {});
        get("/addresses/states").then(({list}) => setEstadoList(list || [])).catch(() => {});
        get("/situacao/list-all").then(({list}) => setSituacaoList(list || [])).catch(() => {});
        get("/complemento-vara/list-all").then(({list}) => setComplementoVaraList(list || [])).catch(() => {});
        buscarCasosSimples("");
    }, []);

    const linhas = [
        { label: 'Valor da Causa', key: 'causa' },
        { label: 'Valor do Pedido', key: 'pedido' },
        { label: 'Resultado', key: 'resultado' }
    ];

    const selectGrupoArea = (selected) => {
        if(selected) get(`/area/${selected.id}/list-all`).then(({list}) => setAreaList(list || [])).catch(() => {});
    };

    const selectJusticaAcao = (code) => {
        if(code) get(`/acao/${code}/list-all`).then(({list}) => setAcaoList(list || [])).catch(() => {});
    };

    const selectEstadoCidade = (selected) => {
        if(selected) get(`/addresses/cities?stateId=${selected.id}`).then(({list}) => setCidadeList(list || [])).catch(() => {});
    };

    const selectCidadeForum = (selected) => {
        if(selected) get(`/forum/${selected.id}/list-all`).then(({list}) => setForumList(list || [])).catch(() => {});
    };

    const calcula_indice = async (init, indice, value) => {
        setLoadingCalc(true)
        if (!indice) {
            errorAlert("É necessário selecionar o tipo de índice!")
            setLoadingCalc(false)
            return;
        }
        if (!init) {
            errorAlert("É necessário informar a data de ínicio!")
            setLoadingCalc(false)
            return;
        }

        // Sanitizing the number: replace comma with dot to match BigDecimal requirements
        let safeValue = value;
        if (typeof value === 'string') {
            safeValue = value.replace(',', '.');
        }

        try {
            const res = await post("/indice/measure", { init, indice, value: Number(safeValue) });
            if (res) return res;
            return 0;
        } catch (err) {
            console.error(err);
            errorAlert("Erro ao calcular índice");
            return 0;
        } finally {
            setLoadingCalc(false);
        }
    };

    return (
        <Card>
            <Formik
                initialValues={{
                    id: id || null,
                    caso: null,
                    area: null,
                    grupoArea: null,
                    relacao: null,
                    numAutomatico: "",
                    statusProcess: null,
                    equipe: null,
                    advogado: null,
                    motivo: null,
                    pagamentosTotais: 0,
                    recebimentosTotais: 0,
                    nprocessoSegundo: null,
                    nprocessoTerceiro: null,
                    npastaCliente: "",
                    justica: null,
                    faseProcesso: null,
                    complementoJustica: null,
                    tipoAcao: null,
                    complemento: null,
                    orgao: null,
                    risco: null,
                    ano: "",
                    dataDistribuicao: null,
                    estado: null,
                    cidade: null,
                    forum: null,
                    vara: "",
                    complementoVara: null,
                    observacao: "",
                    ultCorrecao: null,
                    inicioCorrecao: null,
                    indice: null,
                    situacao: null,
                    justicaGratuita: "",
                    tabelaData: {
                        causa: { nominal: 0, corrigido: 0, corrigidoJuros: 0 },
                        pedido: { nominal: 0, corrigido: 0, corrigidoJuros: 0 },
                        resultado: { nominal: 0, corrigido: 0, corrigidoJuros: 0 }
                    },
                    varaEncontrada: ""
                }}
                validationSchema={Yup.object().shape({
                    caso: Yup.object().required("Campo Obrigatório.").nullable(),
                })}
                onSubmit={(values, actions) => { 
                    const payload = {
                        id: values.id,
                        casoId: values.caso?.id || null,
                        relacao: values.relacao,
                        numAutomatico: values.numAutomatico,
                        status: values.statusProcess?.code,
                        equipe: values.equipe,
                        advogado: values.advogado,
                        numProcessoPrimeira: "",
                        numProcessoSegunda: values.nprocessoSegundo ? (values.nprocessoSegundo.processo || values.nprocessoSegundo.label || values.nprocessoSegundo).replace(/\D/g, "") : "",
                        numProcessoTerceiro: values.nprocessoTerceiro ? (values.nprocessoTerceiro.processo || values.nprocessoTerceiro.label || values.nprocessoTerceiro).replace(/\D/g, "") : "",
                        numPastaCliente: values.npastaCliente,
                        justica: values.justica?.code,
                        complementoJustica: values.complementoJustica?.code,
                        faseProcesso: values.faseProcesso,
                        acao: values.tipoAcao,
                        motivoEncerramento: values.motivo?.code,
                        pagamentosTotais: values.pagamentosTotais,
                        recebimentosTotais: values.recebimentosTotais,
                        orgao: values.orgao,
                        risco: values.risco,
                        valorCausaNominal: values.tabelaData.causa.nominal,
                        valorCausaCorrigido: values.tabelaData.causa.corrigido,
                        valorCausaCorrigidoJuros: values.tabelaData.causa.corrigidoJuros,
                        valorPedidoNominal: values.tabelaData.pedido.nominal,
                        valorPedidoCorrigido: values.tabelaData.pedido.corrigido,
                        valorPedidoCorrigidoJuros: values.tabelaData.pedido.corrigidoJuros,
                        inicioCorrecao: values.inicioCorrecao || null,
                        indice: values.indice?.code || null,
                        ultimaCorrecao: values.ultCorrecao || null,
                        ano: values.ano,
                        dataDistribuicao: values.dataDistribuicao,
                        forum: values.forum,
                        vara: values.vara,
                        observacoes: values.observacao,
                        area: values.area,
                        complementoVara: values.complementoVara,
                        varaEncontrada: values.varaEncontrada,
                        situacao: values.situacao,
                        justicaGratuita: values.justicaGratuita
                    };
                    postNoResponse("/cadastro-basico/save", payload)
                        .then(() => {
                            if (id) {
                                let promises = [];
                                
                                // Se tem 2ª Instância preenchida, vincula (idSegundo -> id)
                                if (values.nprocessoSegundo?.id) {
                                    promises.push(postNoResponse(`/processo/vincular-pai/${values.nprocessoSegundo.id}/${id}`));
                                }
                                
                                // Se tem 3ª Instância preenchida, vincula (idTerceiro -> id)
                                if (values.nprocessoTerceiro?.id) {
                                    promises.push(postNoResponse(`/processo/vincular-pai/${values.nprocessoTerceiro.id}/${id}`));
                                }

                                if (promises.length > 0) {
                                    Promise.all(promises)
                                        .then(() => { if (reloadData) reloadData(); })
                                        .catch(e => console.error("Erro ao vincular filhas", e));
                                } else {
                                    if (reloadData) reloadData();
                                }
                            }
                            successAlert("Salvo com sucesso!");
                            actions.setSubmitting(false);
                        })
                        .catch(() => {
                            errorAlert("Erro ao salvar!");
                            actions.setSubmitting(false);
                        });
                }}
            >
                {({ values, errors, touched, setFieldValue, isSubmitting, handleSubmit }) => (
                    <Form autoComplete="off">
                        <FetchAndSetData 
                            id={id} 
                            setIsLoadingInfo={setIsLoadingInfo} 
                            closeCreateEdit={closeCreateEdit}
                            setAreaList={setAreaList}
                            setAcaoList={setAcaoList}
                            setCidadeList={setCidadeList}
                            setForumList={setForumList}
                            tipoIndiceEnum={tipoIndiceEnum}
                            tipoJusticaEnum={tipoJusticaEnum}
                            tipoComplementoJusticaEnum={tipoComplementoJusticaEnum}
                            motivoEncerramentoEnum={motivoEncerramentoEnum}
                        />

                        <ModalCriarCaso 
                            open={modalCasoOpen} 
                            handleClose={() => setModalCasoOpen(false)} 
                            defaultNomeCaso={modalCasoDefaultNome}
                            onCreated={() => {
                                setModalCasoOpen(false);
                                buscarCasosSimples(modalCasoDefaultNome);
                            }}
                        />

                        <MDBox px={3} pt={5}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Grupo Área</InputLabel>
                                    <MDSelect
                                        isLoading={isLoadingInfo}
                                        getOptionValue={o => o.id}
                                        getOptionLabel={o => o.nome}
                                        value={values.grupoArea}
                                        options={grupoAreaList}
                                        onChange={v => { 
                                            setFieldValue("grupoArea", v); 
                                            setFieldValue("area", null);
                                            selectGrupoArea(v); 
                                        }}
                                        sx={{ minHeight: 38, height: 38 }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={2}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo de Área</InputLabel>
                                    <MDSelect
                                        isLoading={isLoadingInfo}
                                        getOptionValue={o => o.id}
                                        getOptionLabel={o => o.nome}
                                        value={values.area}
                                        options={areaList}
                                        onChange={v => setFieldValue("area", v)}
                                        sx={{ minHeight: 38, height: 38 }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Box display="flex" alignItems="flex-end" gap={1}>
                                        <Box flex={1}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome do Caso</InputLabel>
                                            <MDSelect
                                                creatable
                                                isClearable
                                                isLoading={isLoadingCasosSimples}
                                                options={casosSimplesPesquisa}
                                                getOptionValue={o => o.id || o.nomeCaso || o.value}
                                                getOptionLabel={o => o.nomeCaso || o.label}
                                                value={values.caso}
                                                onChange={v => {
                                                    if (v && v.__isNew__) {
                                                        setModalCasoDefaultNome(v.value);
                                                        setModalCasoOpen(true);
                                                    } else {
                                                        setFieldValue("caso", v);
                                                    }
                                                }}
                                                onInputChange={(val, { action }) => {
                                                    if (action === "input-change") buscarCasosSimplesDebounced(val);
                                                }}
                                                placeholder="Selecione ou adicione..."
                                                formatCreateLabel={(val) => `Cadastrar "${val}"`}
                                                noOptionsMessage={() => "Digite para buscar"}
                                                filterOption={() => true}
                                            />
                                        </Box>
                                        {values.caso && (
                                            <Box>
                                                <MDButton
                                                    variant="outlined"
                                                    color="info"
                                                    disabled={!id}
                                                    title={!id ? "Salve o processo primeiro para vincular contratos" : ""}
                                                    onClick={() => id && handleOpenContratos && handleOpenContratos()}
                                                    sx={{ height: 38 }}
                                                >
                                                    Vincular
                                                </MDButton>
                                            </Box>
                                        )}
                                    </Box>
                                    {errors.caso && touched.caso && (
                                        <MDTypography variant="caption" color="error">
                                            {errors.caso}
                                        </MDTypography>
                                    )}
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Relação</InputLabel>
                                    <MDSelect
                                        isLoading={isLoadingInfo}
                                        getOptionValue={o => o.id}
                                        getOptionLabel={o => o.nome}
                                        value={values.relacao}
                                        options={relacaoList}
                                        onChange={v => setFieldValue("relacao", v)}
                                    />
                                </Grid>
                            </Grid>

                            {isProc && (
                                <Grid container spacing={2} pt={2}>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Justiça Gratuita</InputLabel>
                                        <MDInput
                                            size="small"
                                            fullWidth
                                            value={values.justicaGratuita ?? ""}
                                            onChange={({ target }) => setFieldValue("justicaGratuita", target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Situação</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={o => o.id}
                                            getOptionLabel={o => o.nome}
                                            options={situacaoList}
                                            value={values.situacao}
                                            onChange={v => setFieldValue("situacao", v)}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nº Automático</InputLabel>
                                    <MDInput
                                        size="small"
                                        fullWidth
                                        value={values.numAutomatico ?? ""}
                                        onChange={({ target }) => setFieldValue("numAutomatico", target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                                    <MDSelect
                                        isLoading={isLoadingInfo}
                                        options={statusProcesso}
                                        getOptionValue={o => o.code}
                                        getOptionLabel={o => o.name}
                                        value={values.statusProcess}
                                        onChange={v => setFieldValue("statusProcess", v)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Equipe Responsável</InputLabel>
                                    <MDSelect
                                        isLoading={isLoadingInfo}
                                        options={equipeList}
                                        getOptionValue={o => o.id}
                                        getOptionLabel={o => o.nome}
                                        value={values.equipe}
                                        onChange={v => {
                                            setFieldValue("equipe", v);
                                            setFieldValue("advogado", null);
                                        }}
                                    />
                                </Grid>
                                <AdvogadoSelector
                                    isLoadingInfo={isLoadingInfo}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                />
                            </Grid>

                            {values.statusProcess?.code === 'closed' && (
                                <Grid container spacing={2} pt={2}>
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Motivo</InputLabel>
                                        <MDSelect
                                            getOptionValue={o => o.code}
                                            getOptionLabel={o => o.name}
                                            options={motivoEncerramentoEnum}
                                            value={values.motivo}
                                            onChange={v => setFieldValue("motivo", v)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Pagamentos (Totais)</InputLabel>
                                        <MDInput
                                            size="small"
                                            fullWidth
                                            type='number'
                                            value={values.pagamentosTotais ?? 0}
                                            onChange={({ target }) => setFieldValue("pagamentosTotais", target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Recebimentos (Totais)</InputLabel>
                                        <MDInput
                                            size="small"
                                            fullWidth
                                            type='number'
                                            value={values.recebimentosTotais ?? 0}
                                            onChange={({ target }) => setFieldValue("recebimentosTotais", target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            <Grid container spacing={2} pt={2}>
                                <Grid item xs={12} sm={6}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nº Processo (2ª Instância)</InputLabel>
                                    <MDSelect
                                        creatable
                                        isClearable
                                        isLoading={isLoadingProcessos}
                                        options={processosPesquisa}
                                        getOptionValue={o => o.id || o.processo || o.value}
                                        getOptionLabel={o => o.processo || o.label}
                                        value={values.nprocessoSegundo}
                                        onChange={v => setFieldValue("nprocessoSegundo", v)}
                                        onInputChange={(val, { action }) => {
                                            if (action === "input-change") buscarProcessosDebounced(val);
                                        }}
                                        placeholder="Pesquise o processo..."
                                        formatCreateLabel={(inputValue) => `Adicionar manual: "${inputValue}"`}
                                        noOptionsMessage={() => "Digite para buscar"}
                                        filterOption={() => true}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nº Processo (3ª Instância)</InputLabel>
                                    <MDSelect
                                        creatable
                                        isClearable
                                        isLoading={isLoadingProcessos}
                                        options={processosPesquisa}
                                        getOptionValue={o => o.id || o.processo || o.value}
                                        getOptionLabel={o => o.processo || o.label}
                                        value={values.nprocessoTerceiro}
                                        onChange={v => setFieldValue("nprocessoTerceiro", v)}
                                        onInputChange={(val, { action }) => {
                                            if (action === "input-change") buscarProcessosDebounced(val);
                                        }}
                                        placeholder="Pesquise o processo..."
                                        formatCreateLabel={(inputValue) => `Adicionar manual: "${inputValue}"`}
                                        noOptionsMessage={() => "Digite para buscar"}
                                        filterOption={() => true}
                                    />
                                </Grid>
                            </Grid>

                            <Divider />

                            <Grid container spacing={2} pt={2}>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nº Pasta (Cliente)</InputLabel>
                                    <MDInput size="small" fullWidth value={values.npastaCliente ?? ""} onChange={({ target }) => setFieldValue("npastaCliente", target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Justiça</InputLabel>
                                    <MDSelect getOptionValue={o => o.code} getOptionLabel={o => o.name} options={tipoJusticaEnum} value={values.justica} onChange={v => { setFieldValue("justica", v); setFieldValue("tipoAcao", null); selectJusticaAcao(v?.code); }} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Complemento Justiça</InputLabel>
                                    <MDSelect getOptionValue={o => o.code} getOptionLabel={o => o.name} options={tipoComplementoJusticaEnum} value={values.complementoJustica} onChange={v => setFieldValue("complementoJustica", v)} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo de Ação</InputLabel>
                                    <MDSelect options={acaoList} getOptionValue={o => o.id} getOptionLabel={o => o.nome} value={values.tipoAcao} onChange={v => setFieldValue("tipoAcao", v)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Órgão</InputLabel>
                                    <MDSelect options={orgaoList} getOptionValue={o => o.id} getOptionLabel={o => o.nome} value={values.orgao} onChange={v => setFieldValue("orgao", v)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Risco</InputLabel>
                                    <MDSelect options={riscoList} getOptionValue={o => o.id} getOptionLabel={o => o.descricao} value={values.risco} onChange={v => setFieldValue("risco", v)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Fase Processo</InputLabel>
                                    <MDSelect options={faseProcessoList} getOptionValue={o => o.id} getOptionLabel={o => o.descricao} value={values.faseProcesso} onChange={v => setFieldValue("faseProcesso", v)} />
                                </Grid>
                            </Grid>

                            <Divider sx={{ mt: 3, mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                    <MDBox sx={{ border: darkMode ? '1px solid #444' : '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                                        <Grid container sx={{ backgroundColor: '#a86b32' }}>
                                            <Grid item xs={3} sx={{ textAlign: 'center', py: 1 }}><MDTypography variant="button" color="white" sx={{ fontWeight: 'bold', fontSize: '11px' }}>Valores</MDTypography></Grid>
                                            <Grid item xs={3} sx={{ textAlign: 'center', py: 1, borderLeft: '1px solid #fff' }}><MDTypography variant="button" color="white" sx={{ fontWeight: 'bold', fontSize: '11px' }}>Nominal</MDTypography></Grid>
                                            <Grid item xs={3} sx={{ textAlign: 'center', py: 1, borderLeft: '1px solid #fff' }}><MDTypography variant="button" color="white" sx={{ fontWeight: 'bold', fontSize: '11px' }}>Corrigido</MDTypography></Grid>
                                            <Grid item xs={3} sx={{ textAlign: 'center', py: 1, borderLeft: '1px solid #fff' }}><MDTypography variant="button" color="white" sx={{ fontWeight: 'bold', fontSize: '11px' }}>Corrig. c/ Juros</MDTypography></Grid>
                                        </Grid>

                                        {linhas.map(linha => (
                                            <Grid container key={linha.key} sx={{ borderTop: darkMode ? '1px solid #444' : '1px solid #ccc' }}>
                                                <Grid item xs={3} sx={{ px: 1, py: 0.2, backgroundColor: darkMode ? '#2c2c3e' : '#white', display: 'flex', alignItems: 'center' }}>
                                                    <MDTypography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>{linha.label}</MDTypography>
                                                </Grid>
                                                <Grid item xs={3} sx={{ p: '1px', borderLeft: darkMode ? '1px solid #444' : '1px solid #ccc' }}>
                                                    {linha.key === 'resultado' ? (
                                                        <MDBox sx={{ p: 0.2, textAlign: 'center', backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <MDTypography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatCurrency(values.tabelaData?.[linha.key]?.nominal)}</MDTypography>
                                                        </MDBox>
                                                    ) : (
                                                        <MDInput 
                                                            size="small" 
                                                            type="text" 
                                                            value={formatCurrency(values.tabelaData?.[linha.key]?.nominal || "0")} 
                                                            onChange={({ target }) => {
                                                                let numeric = target.value.replace(/\D/g, "");
                                                                if (!numeric) numeric = "0";
                                                                setFieldValue(`tabelaData.${linha.key}.nominal`, (parseInt(numeric, 10) / 100).toFixed(2));
                                                            }}
                                                            fullWidth 
                                                            inputProps={{ sx: { height: '26px', fontSize: '0.7rem', textAlign: 'center' } }}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs={3} sx={{ p: '1px', borderLeft: darkMode ? '1px solid #444' : '1px solid #ccc' }}>
                                                    <MDBox sx={{ p: 0.2, textAlign: 'center', backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <MDTypography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatCurrency(values.tabelaData?.[linha.key]?.corrigido)}</MDTypography>
                                                    </MDBox>
                                                </Grid>
                                                <Grid item xs={3} sx={{ p: '1px', borderLeft: darkMode ? '1px solid #444' : '1px solid #ccc' }}>
                                                    <MDBox sx={{ p: 0.2, textAlign: 'center', backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <MDTypography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatCurrency(values.tabelaData?.[linha.key]?.corrigidoJuros)}</MDTypography>
                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <InputLabel sx={{ px: 1, pb: 0.5 }}>Índice</InputLabel>
                                            <MDSelect getOptionValue={o => o.code} options={tipoIndiceEnum} getOptionLabel={o => o.name} value={values.indice} onChange={v => setFieldValue("indice", v)} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5 }}>Data Início</InputLabel>
                                            <MDTextFieldCalendar type="date" value={values.inicioCorrecao ?? ""} fullWidth onChange={({ target }) => setFieldValue("inicioCorrecao", target.value)} darkMode={darkMode} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5 }}>Última Correção</InputLabel>
                                            <MDTextFieldCalendar type="date" disabled value={values.ultCorrecao ?? ""} fullWidth darkMode={darkMode} />
                                        </Grid>
                                        <Grid item xs={12} display="flex" gap={1}>
                                            <MDButton fullWidth size="small" variant="contained" color="info" onClick={async () => {
                                                const val = await calcula_indice(values.inicioCorrecao, values.indice?.code, values.tabelaData.causa.nominal);
                                                setFieldValue("tabelaData.causa.corrigido", val);
                                                setFieldValue("ultCorrecao", toDateReqFormat(new Date()));
                                            }} endIcon={isLoadingCalc ? <CircularProgress color="inherit" size={16} /> : null}>
                                                Calcular
                                            </MDButton>
                                            <MDButton fullWidth size="small" variant="outlined" color="info" onClick={() => {}}>
                                                <Icon sx={{ mr: 1 }}>history</Icon> Histórico
                                            </MDButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} pt={2}>
                                <Grid item xs={12} sm={1}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Ano</InputLabel>
                                    <MDInput size="small" fullWidth value={values.ano ?? ""} onChange={({ target }) => setFieldValue("ano", target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Data Distribuição</InputLabel>
                                    <MDTextFieldCalendar type="date" value={values.dataDistribuicao ?? ""} fullWidth onChange={({ target }) => setFieldValue("dataDistribuicao", target.value)} darkMode={darkMode} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Estado</InputLabel>
                                    <MDSelect options={estadoList} getOptionValue={o => o.id} getOptionLabel={o => o.descricao} value={values.estado} onChange={v => { setFieldValue("estado", v); setFieldValue("cidade", null); setFieldValue("forum", null); selectEstadoCidade(v); }} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Comarca</InputLabel>
                                    <MDSelect options={cidadeList} getOptionValue={o => o.id} getOptionLabel={o => o.descricao} value={values.cidade} onChange={v => { setFieldValue("cidade", v); setFieldValue("forum", null); selectCidadeForum(v); }} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Fórum</InputLabel>
                                    <MDSelect options={forumList} getOptionValue={o => o.id} getOptionLabel={o => o.nome} value={values.forum} onChange={v => setFieldValue("forum", v)} />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} pt={2}>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Vara</InputLabel>
                                    <MDInput size="small" type="number" fullWidth value={values.vara ?? ""} onChange={({ target }) => setFieldValue("vara", target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Complemento Vara</InputLabel>
                                    <MDSelect options={complementoVaraList} getOptionValue={o => o.id} getOptionLabel={o => o.descricao} value={values.complementoVara} onChange={v => setFieldValue("complementoVara", v)} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Vara Encontrada</InputLabel>
                                    <MDInput disabled size="small" fullWidth value={values.varaEncontrada ?? ""} sx={disabledStyle} />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} pt={2}>
                                <Grid item xs={12}>
                                    <InputLabel sx={{ px: 1, pb: 0.5 }}>Observações</InputLabel>
                                    <MDInput size="small" fullWidth value={values.observacao ?? ""} onChange={({ target }) => setFieldValue("observacao", target.value)} />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3 }} />

                            <Box display="flex" justifyContent="end" p={2}>
                                <MDButton 
                                    onClick={() => {
                                        if (Object.keys(errors).length > 0) {
                                            errorAlert(`Existem campos pendentes: ${Object.values(errors).join(", ")}`);
                                        }
                                        handleSubmit();
                                    }} 
                                    variant="gradient" 
                                    color="info" 
                                    disabled={isSubmitting}
                                >
                                    Salvar Cadastro Básico
                                </MDButton>
                            </Box>
                        </MDBox>
                    </Form>
                )}
            </Formik>
        </Card>
    );
}
